/**
 * src/lib/ai-client.ts
 *
 * Thin client-side wrapper for calling the /api/ai route.
 * Import this in 'use client' components instead of importing
 * genkit flows directly.
 *
 * Usage:
 *   import { callAiFlow } from '@/lib/ai-client';
 *   const result = await callAiFlow('diagnose-car-problems', { symptoms: '...' });
 */

export type AiFlowName =
  | 'diagnose-car-problems'
  | 'lookup-vin'
  | 'analyze-inspection'
  | 'analyze-car-sound'
  | 'car-service-suggestions'
  | 'car-expert-chat'
  | 'part-expert-chat'
  | 'personalized-recommendations';

export class AiError extends Error {
  constructor(message: string, public readonly status?: number) {
    super(message);
    this.name = 'AiError';
  }
}

export async function callAiFlow<TInput, TOutput>(
  flow: AiFlowName,
  input: TInput,
  options?: { timeoutMs?: number }
): Promise<TOutput> {
  const controller = new AbortController();
  const timeoutId = setTimeout(
    () => controller.abort(),
    options?.timeoutMs ?? 30_000
  );

  try {
    const res = await fetch('/api/ai', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ flow, input }),
      signal: controller.signal,
    });

    const data = await res.json();

    if (!res.ok) {
      throw new AiError(data?.error || `AI error: ${res.status}`, res.status);
    }

    return data.result as TOutput;
  } catch (err: any) {
    if (err.name === 'AbortError') {
      throw new AiError('انتهت مهلة الاتصال بالذكاء الاصطناعي. حاول مجدداً.');
    }
    throw err;
  } finally {
    clearTimeout(timeoutId);
  }
}
